import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Typography,
  Container,
  Divider,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import useAuth from '../navigation/AuthContext';
import { APIService, setEnvironment } from '../utils/api';
import InputField from '../common/InputField';
import SelectField from '../common/SelectField';
import { loginValidationSchema, ROUTE, ENVIRONMENT_OPTIONS, STORAGE_KEY } from '../utils/constants';
import RetrackLogo from '../assets/retrack-logo.png';

export const LayoutWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '20px',
  fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
});

export const LogoWrapper = styled(Box)({
  marginBottom: '40px',
  display: 'flex',
  justifyContent: 'center',
  '& img': {
    maxHeight: '80px',
    width: 'auto',
    objectFit: 'contain',
  }
});

export const StyledCard = styled(Card)({
  borderRadius: '12px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  maxWidth: '450px',
  width: '100%',
  padding: '40px 32px',
  backgroundColor: '#ffffff',
  '&:hover': {
    boxShadow: '0 25px 70px rgba(0, 0, 0, 0.35)',
    transition: 'box-shadow 0.3s ease',
  }
});

export const FormHeader = styled(Box)({
  marginBottom: '30px',
  textAlign: 'center',
  '& h2': {
    margin: '0 0 8px 0',
    fontSize: '28px',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  '& p': {
    margin: 0,
    fontSize: '14px',
    color: '#666',
    fontWeight: 400,
  }
});

export const StyledEnvironmentField = styled(Box)({
  marginBottom: '20px',
  padding: '12px',
  backgroundColor: '#f8f9ff',
  borderRadius: '8px',
  border: '1px solid #e0e6f7',
});

const Login = () => {
  const history = useHistory();
  const auth = useAuth();
  const [loginError, setLoginError] = useState('');
  const [initValues, setInitValues] = useState({ 
    email: '', 
    password: '', 
    role: 'admin',
    environment: 'retrackDev' 
  });

  useEffect(() => {
    const savedEnvironment = localStorage.getItem(STORAGE_KEY.environment) || 'retrackDev';
    setInitValues(prev => ({
      ...prev,
      environment: savedEnvironment
    }));
  }, []);

  const login = async (values) => {
    setLoginError('');
    try {
      // Set the environment based on user selection
      setEnvironment(values.environment);
      // Save environment to localStorage
      localStorage.setItem(STORAGE_KEY.environment, values.environment);

      const res = await APIService.userLogin(values);
      if (!res.success) throw res;
      if (res.result.token) {
        auth.signin(res.result);
        history.replace(ROUTE.app);
      }
    } catch (error) {
      setLoginError('Invalid credentials, Please try again!');
      console.log('Login error', error.message);
    }
  };

  return (
    <LayoutWrapper>
      <Container maxWidth="sm">
        <LogoWrapper>
          <img src={RetrackLogo} alt="ReTrack Logo" />
        </LogoWrapper>

        <StyledCard>
          <FormHeader>
            <Typography component="h2" variant="h5">
              Welcome Back
            </Typography>
            <Typography component="p" sx={{ fontSize: '14px', color: '#888', mt: 1 }}>
              Sign in to your ReTrack Backoffice account
            </Typography>
          </FormHeader>

          {loginError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {loginError}
            </Alert>
          )}

          <Formik
            initialValues={initValues}
            validationSchema={loginValidationSchema}
            onSubmit={login}
            enableReinitialize
          >
            {({ handleSubmit, isSubmitting, values }) => (
              <form onSubmit={handleSubmit}>
                <StyledEnvironmentField>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667eea', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Environment
                  </Typography>
                  <SelectField
                    name="environment"
                    label="Select Environment"
                    data={ENVIRONMENT_OPTIONS}
                    margin="normal"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />
                </StyledEnvironmentField>

                <InputField
                  name="email"
                  label="Email Address"
                  type="email"
                  margin="normal"
                  fullWidth
                  disabled={isSubmitting}
                  placeholder="Enter your email"
                  sx={{
                    marginBottom: '16px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#fafbfc',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />

                <InputField
                  name="password"
                  label="Password"
                  type="password"
                  margin="normal"
                  fullWidth
                  disabled={isSubmitting}
                  placeholder="Enter your password"
                  sx={{
                    marginBottom: '24px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#fafbfc',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />

                <Box sx={{ py: 1, textAlign: 'center' }}>
                  {isSubmitting ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '44px' }}>
                      <CircularProgress size={24} sx={{ color: '#667eea' }} />
                    </Box>
                  ) : (
                    <Button
                      fullWidth
                      size="large"
                      type="submit"
                      sx={{
                        py: 1.5,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '16px',
                        borderRadius: '8px',
                        textTransform: 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                      variant="contained"
                    >
                      Sign In
                    </Button>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#999',
                    textAlign: 'center',
                    fontWeight: 400,
                  }}
                >
                  Environment: <strong>{values.environment ? ENVIRONMENT_OPTIONS.find(e => e.value === values.environment)?.label : 'Not selected'}</strong>
                </Typography>
              </form>
            )}
          </Formik>
        </StyledCard>

        <Typography
          sx={{
            mt: 4,
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.8)',
            textAlign: 'center',
          }}
        >
          © 2024 ReTrack. All rights reserved. v2.0.0
        </Typography>
      </Container>
    </LayoutWrapper>
  );
};

export default Login;
